import React from 'react';
import { useIntl } from 'react-intl';

import { UserActivity } from '~/components/Shared/UserActivity/UserActivity';
import { Checkbox } from '~/eds/src/components/Checkbox/Checkbox';
import { CollapsibleText } from '~/eds/src/components/CollapsibleText/CollapsibleText';
import { Footer } from '~/eds/src/components/Footer/Footer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Link } from '~/eds/src/components/Link/Link';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import { Variation } from '../../../../types';

interface Props {
  isSelected?: boolean;
  variation: Variation;
}

const p = 4;

export const ClauseVariation = ({ isSelected = false, variation }: Props) => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const {
    content,
    contractsCount,
    id,
    name,
    generatedBy,
    generatedOn,
    variationsCount,
  } = variation;

  const actions = [
    {
      level: 'tertiary' as const,
      text: intl.formatMessage({
        id: 'ClauseVariation.deleteAction3',
        defaultMessage: 'Delete',
        description: 'Delete button text',
      }),
      onClick: console.log,
    },
    {
      level: 'secondary' as const,
      text: intl.formatMessage({
        id: 'ClauseVariation.moveToReviewAction',
        defaultMessage: 'Move to Review',
        description: 'Move to review button text',
      }),
      onClick: console.log,
    },
  ];

  const contractsCountText = formatNumber(contractsCount);
  const variationsCountText = formatNumber(variationsCount);

  const checkbox = (
    <Checkbox
      name={name}
      value={isSelected}
      option={{
        label: '',
        value: id,
      }}
      onChange={console.log}
    />
  );

  const moreVariations = variationsCount > 0 && (
    <StatusMessage
      action={{
        text: intl.formatMessage(
          {
            id: 'ClauseVariation.showAllVariationsAction',
            defaultMessage: 'Show All {variationsCount} Variations',
            description:
              'Show all variations button text. Example: variationsCount = "25"',
          },
          { variationsCount: variationsCountText },
        ),
        onClick: console.log,
      }}
      message={intl.formatMessage(
        {
          id: 'ClauseVariation.variationsMessage',
          defaultMessage:
            '{variationsCount} Variations across {contractsCount} Contracts',
          description:
            'Status message showing variations and contracts count. Example: variationsCount = "25", contractsCount = "150"',
        },
        {
          variationsCount: variationsCountText,
          contractsCount: contractsCountText,
        },
      )}
    />
  );

  const footer = <Footer actions={actions} enableBorder={false} />;

  return (
    <Layout border="border.divider" direction="column" pt={p} spacing={p}>
      <Layout flex="auto" px={p} spacing={p}>
        {checkbox}
        <Layout direction="column" flex="auto" spacing={p}>
          <Layout direction="column" spacing={2}>
            <Text variant="body-bold">{name}</Text>
            <Link pathname="/">
              {intl.formatMessage(
                {
                  id: 'ClauseVariation.appearsInContractsLink',
                  defaultMessage: 'Appears in {contractsCount} contracts',
                  description:
                    'Link text showing contract count. Example: contractsCount = "150"',
                },
                { contractsCount: contractsCountText },
              )}
            </Link>
            <UserActivity
              activity="Generated"
              id={generatedBy}
              date={generatedOn}
            />
            <CollapsibleText text={content} />
          </Layout>
          {moreVariations}
        </Layout>
      </Layout>
      {footer}
    </Layout>
  );
};
